import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  TextField, 
  CircularProgress, 
  Alert,
  Card,
  CardContent,
  Grid,
  Container,
  useTheme,
  Chip
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Document, Paragraph, TextRun, HeadingLevel, AlignmentType, Packer } from 'docx';
import { saveAs } from 'file-saver';
import { 
  Gavel as GavelIcon,
  Person as PersonIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Description as DescriptionIcon,
  CloudUpload as CloudUploadIcon,
  AutoAwesome as AutoAwesomeIcon,
  Chat as ChatIcon,
  ContentCopy as ContentCopyIcon,
  SmartToy as RobotIcon
} from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';
import { 
  uploadDefenseTemplate, 
  uploadCaseFile, 
  generateDefenseStream,
  getUploadedCaseData
} from '../services/api';
import { similarCasesToAccords } from '../services/similarCases';
import SelectedAccordsGrid from './SelectedAccordsGrid';

function Defesa({ selectedAccords, onPreselectAccords, onRemoveAccord }) {
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const [caseDescription, setCaseDescription] = useState('');
  const [defenseMessages, setDefenseMessages] = useState([]);
  const [agentMessages, setAgentMessages] = useState([]);
  const [generatedDefense, setGeneratedDefense] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadingTemplate, setUploadingTemplate] = useState(false);
  const [uploadingCase, setUploadingCase] = useState(false);
  const [templateUploaded, setTemplateUploaded] = useState(false);
  const [caseUploaded, setCaseUploaded] = useState(false);
  const [templateFileName, setTemplateFileName] = useState('');
  const [caseFileName, setCaseFileName] = useState('');
  const theme = useTheme();

  // Function to get display name for different agent roles
  const getAgentDisplayName = (role) => {
    const roleMap = {
      'assistant': 'Agente de Defesa Jurídica',
      'system': 'Sistema',
      'user': 'Usuário',
      'analyst': 'Agente de Análise',
      'researcher': 'Agente de Pesquisa',
      'writer': 'Agente de Redação',
      'reviewer': 'Agente de Revisão',
      'legal_analyst': 'Agente de Análise Jurídica',
      'case_analyst': 'Agente de Análise de Casos',
      'defense_writer': 'Agente de Redação de Defesa',
      'precedent_researcher': 'Agente de Pesquisa de Precedentes',
      // Specific agents used in your system
      'defense_structure_interpreter': 'Intérprete de Estrutura de Defesa',
      'case_interpreter': 'Intérprete de Casos',
      'legal_searcher': 'Pesquisador Jurídico',
      'defense_creator': 'Criador de Defesa',
      'supervisor': 'Supervisor'
    };
    
    // Handle null/undefined roles
    if (!role) {
      return 'Agente do Sistema';
    }
    
    // Return mapped name or a formatted version of the role
    return roleMap[role] || `Agente ${role.charAt(0).toUpperCase() + role.slice(1).replace(/_/g, ' ')}`;
  };

  if (!isAuthenticated) {
    return (
      <Card sx={{ textAlign: 'center', mt: 4 }}>
        <CardContent sx={{ p: 4 }}>
          <GavelIcon sx={{ fontSize: 64, color: theme.palette.primary.main, mb: 2 }} />
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Acesso Restrito
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Para gerar defesas administrativas, você precisa estar autenticado no sistema.
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => loginWithRedirect()}
            startIcon={<PersonIcon />}
            size="large"
          >
            Fazer Login
          </Button>
        </CardContent>
      </Card>
    );
  }

  const handleDefenseTemplateUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingTemplate(true);
    setError(null);

    try {
      await uploadDefenseTemplate(file);
      setTemplateUploaded(true);
      setTemplateFileName(file.name);
    } catch (err) {
      setError(`Erro ao carregar modelo de defesa: ${err.message}`);
    } finally {
      setUploadingTemplate(false);
    }
  };

  const handleCaseFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingCase(true);
    setError(null);

    try {
      await uploadCaseFile(file);
      setCaseUploaded(true);
      setCaseFileName(file.name);
      // Pre-select the decisions most similar to the uploaded case as context for the defense
      if (onPreselectAccords) {
        const uploaded = await getUploadedCaseData();
        onPreselectAccords(similarCasesToAccords(uploaded));
      }
    } catch (err) {
      setError(`Erro ao carregar caso: ${err.message}`);
    } finally {
      setUploadingCase(false);
    }
  };

  const handleGenerateDefense = async () => {
    // Check if any input was provided
    if (!templateUploaded && !caseUploaded && !caseDescription.trim() && selectedAccords.length === 0) {
      setError('Por favor, forneça pelo menos um arquivo, descrição ou selecione acórdãos.');
      return;
    }

    setLoading(true);
    setError(null);
    setDefenseMessages([]);
    setAgentMessages([]);
    setGeneratedDefense('');

    try {
      let finalDefenseContent = '';
      
      // Enhanced debugging for API call
      const apiPayload = {
        human_message: caseDescription || 'Elabore a defesa administrativa para o caso fornecido pelo usuário.',
        selected_cases_ids: selectedAccords.map(accord => accord.id),
      };
      
      console.log('⚖️ DEFENSE GENERATION API DEBUG:');
      console.log('📊 Selected accords count:', selectedAccords.length);
      console.log('🔍 Selected accord IDs:', apiPayload.selected_cases_ids);
      console.log('📤 Full API payload:', JSON.stringify(apiPayload, null, 2));
      
      if (selectedAccords.length > 0) {
        console.log('📋 Sample accord object:', selectedAccords[0]);
      }
      
      await generateDefenseStream(
        apiPayload,
        (message) => {
          // Clean code block markers from message content
          const content = message.content
            .replace(/^```(?:\w+)?\n?/, '')  // Remove leading ``` or ```lang
            .replace(/\n?```$/, '');        // Remove trailing ```
          
          const cleanMessage = { ...message, content };
          
          // Add to agent messages for display
          setAgentMessages(prev => [...prev, cleanMessage]);
          
          // Also keep in defense messages for compatibility
          setDefenseMessages(prev => [...prev, cleanMessage]);
          
          // If this looks like the final defense document, set it
          if (content.length > 200 && (content.includes('DEFESA') || content.includes('RECURSO'))) {
            finalDefenseContent = content;
            setGeneratedDefense(content);
          }
        }
      );
      
      // If we didn't capture a final defense, use the last substantial message
      if (!finalDefenseContent && defenseMessages.length > 0) {
        const lastMessage = defenseMessages[defenseMessages.length - 1];
        if (lastMessage && lastMessage.content && lastMessage.content.length > 100) {
          setGeneratedDefense(lastMessage.content);
        }
      }
      
    } catch (err) {
      setError('Falha ao gerar a defesa administrativa. Tente novamente mais tarde.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyToClipboard = () => {
    if (generatedDefense) {
      navigator.clipboard.writeText(generatedDefense);
    } else {
      const allContent = defenseMessages.map(msg => `${msg.role}: ${msg.content}`).join('\n\n');
      navigator.clipboard.writeText(allContent);
    }
  };

  const handleDownloadDefense = async () => {
    const content = generatedDefense || defenseMessages.map(msg => `${msg.role}: ${msg.content}`).join('\n\n');
    
    // Parse markdown content and convert to Word document
    const paragraphs = [];
    const lines = content.split('\n');
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      
      // Skip empty lines
      if (!line.trim()) {
        paragraphs.push(new Paragraph({ text: '' }));
        continue;
      }
      
      // Handle headers
      if (line.startsWith('# ')) {
        paragraphs.push(new Paragraph({
          text: line.replace(/^# /, ''),
          heading: HeadingLevel.HEADING_1,
          spacing: { before: 240, after: 120 }
        }));
      } else if (line.startsWith('## ')) {
        paragraphs.push(new Paragraph({
          text: line.replace(/^## /, ''),
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 200, after: 100 }
        }));
      } else if (line.startsWith('### ')) {
        paragraphs.push(new Paragraph({
          text: line.replace(/^### /, ''),
          heading: HeadingLevel.HEADING_3,
          spacing: { before: 160, after: 80 }
        }));
      } else if (line.startsWith('#### ')) {
        paragraphs.push(new Paragraph({
          text: line.replace(/^#### /, ''),
          heading: HeadingLevel.HEADING_4,
          spacing: { before: 120, after: 60 }
        }));
      }
      // Handle bullet points
      else if (line.match(/^[\s]*[-*+]\s/)) {
        const text = line.replace(/^[\s]*[-*+]\s/, '');
        const textRuns = parseInlineFormatting(text);
        paragraphs.push(new Paragraph({
          children: textRuns,
          bullet: { level: 0 },
          spacing: { before: 60, after: 60 }
        }));
      }
      // Handle numbered lists
      else if (line.match(/^[\s]*\d+\.\s/)) {
        const text = line.replace(/^[\s]*\d+\.\s/, '');
        const textRuns = parseInlineFormatting(text);
        paragraphs.push(new Paragraph({
          children: textRuns,
          numbering: { reference: 'default-numbering', level: 0 },
          spacing: { before: 60, after: 60 }
        }));
      }
      // Handle blockquotes
      else if (line.startsWith('> ')) {
        const text = line.replace(/^> /, '');
        const textRuns = parseInlineFormatting(text);
        paragraphs.push(new Paragraph({
          children: textRuns,
          italics: true,
          indent: { left: 720 },
          spacing: { before: 100, after: 100 }
        }));
      }
      // Regular paragraphs
      else {
        const textRuns = parseInlineFormatting(line);
        paragraphs.push(new Paragraph({
          children: textRuns,
          spacing: { before: 100, after: 100 }
        }));
      }
    }
    
    // Helper function to parse inline formatting (bold, italic, code)
    function parseInlineFormatting(text) {
      const runs = [];
      let currentText = '';
      let i = 0;
      
      while (i < text.length) {
        // Check for bold (**text** or __text__)
        if ((text[i] === '*' && text[i + 1] === '*') || (text[i] === '_' && text[i + 1] === '_')) {
          if (currentText) {
            runs.push(new TextRun({ text: currentText }));
            currentText = '';
          }
          const delimiter = text[i];
          i += 2;
          let boldText = '';
          while (i < text.length - 1 && !(text[i] === delimiter && text[i + 1] === delimiter)) {
            boldText += text[i];
            i++;
          }
          if (i < text.length - 1) {
            runs.push(new TextRun({ text: boldText, bold: true }));
            i += 2;
          } else {
            currentText += delimiter + delimiter + boldText;
          }
        }
        // Check for italic (*text* or _text_)
        else if (text[i] === '*' || text[i] === '_') {
          if (currentText) {
            runs.push(new TextRun({ text: currentText }));
            currentText = '';
          }
          const delimiter = text[i];
          i++;
          let italicText = '';
          while (i < text.length && text[i] !== delimiter) {
            italicText += text[i];
            i++;
          }
          if (i < text.length) {
            runs.push(new TextRun({ text: italicText, italics: true }));
            i++;
          } else {
            currentText += delimiter + italicText;
          }
        }
        // Check for inline code (`code`)
        else if (text[i] === '`') {
          if (currentText) {
            runs.push(new TextRun({ text: currentText }));
            currentText = '';
          }
          i++;
          let codeText = '';
          while (i < text.length && text[i] !== '`') {
            codeText += text[i];
            i++;
          }
          if (i < text.length) {
            runs.push(new TextRun({ text: codeText, font: 'Courier New', shading: { fill: 'E0E0E0' } }));
            i++;
          } else {
            currentText += '`' + codeText;
          }
        }
        else {
          currentText += text[i];
          i++;
        }
      }
      
      if (currentText) {
        runs.push(new TextRun({ text: currentText }));
      }
      
      return runs.length > 0 ? runs : [new TextRun({ text: '' })];
    }
    
    // Create Word document
    const doc = new Document({
      sections: [{
        properties: {},
        children: paragraphs
      }],
      numbering: {
        config: [
          {
            reference: 'default-numbering',
            levels: [
              {
                level: 0,
                format: 'decimal',
                text: '%1.',
                alignment: AlignmentType.LEFT
              }
            ]
          }
        ]
      }
    });
    
    // Generate and download the document
    try {
      const blob = await Packer.toBlob(doc);
      saveAs(blob, 'defesa_administrativa.docx');
    } catch (error) {
      console.error('Error generating DOCX file:', error);
      // Fallback to text file if DOCX generation fails
      const element = document.createElement('a');
      const file = new Blob([content], {type: 'text/plain'});
      element.href = URL.createObjectURL(file);
      element.download = 'defesa_administrativa.txt';
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
    }
  };

  return (
    <Container maxWidth={false} sx={{ px: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h3" 
          sx={{ 
            fontWeight: 700,
            mb: 1,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
            backgroundClip: 'text',
            textFillColor: 'transparent',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <GavelIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
          Defesa Administrativa
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Faça upload de um modelo de defesa, caso ou descreva o caso para gerar defesas administrativas automatizadas
        </Typography>
      </Box>

      {/* Upload Sections */}
      <Grid container spacing={3} sx={{ mb: 4, width: '100%' }}>
        {/* Combined Upload Section - Template and Case File */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              {/* Modelo de Defesa Section */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <DescriptionIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Modelo de Defesa
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Faça upload do modelo de defesa (DOCX, PDF, ou TXT)
                </Typography>
                
                <input
                  accept=".docx,.pdf,.txt"
                  style={{ display: 'none' }}
                  id="defense-template-upload"
                  type="file"
                  onChange={handleDefenseTemplateUpload}
                />
                <label htmlFor="defense-template-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    fullWidth
                    startIcon={uploadingTemplate ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                    disabled={uploadingTemplate}
                    sx={{ mb: 1 }}
                  >
                    {uploadingTemplate ? 'Carregando...' : 'Selecionar Arquivo'}
                  </Button>
                </label>
                
                {templateUploaded && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    Modelo de defesa carregado com sucesso!
                    {templateFileName && (
                      <Box sx={{ mt: 1 }}>
                        <Chip 
                          label={templateFileName} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                      </Box>
                    )}
                  </Alert>
                )}
              </Box>

              {/* Arquivo do Caso Section */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <UploadIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Arquivo do Caso
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Faça upload do caso (DOCX, PDF, ou TXT)
                </Typography>
                
                <input
                  accept=".docx,.pdf,.txt"
                  style={{ display: 'none' }}
                  id="case-file-upload"
                  type="file"
                  onChange={handleCaseFileUpload}
                />
                <label htmlFor="case-file-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    fullWidth
                    startIcon={uploadingCase ? <CircularProgress size={20} /> : <CloudUploadIcon />}
                    disabled={uploadingCase}
                    sx={{ mb: 1 }}
                  >
                    {uploadingCase ? 'Carregando...' : 'Selecionar Arquivo'}
                  </Button>
                </label>
                
                {caseUploaded && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    Caso carregado com sucesso!
                    {caseFileName && (
                      <Box sx={{ mt: 1 }}>
                        <Chip 
                          label={caseFileName} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                      </Box>
                    )}
                  </Alert>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Case Description - Taking remaining width */}
        <Grid item xs={12} md={6}>
          <Card sx={{ width: '100%' }}>
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DescriptionIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Descrição do Caso
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Descreva o caso (opcional)
              </Typography>
              
              <TextField
                fullWidth
                multiline
                rows={8}
                variant="outlined"
                placeholder="Descreva o caso aqui... Esta descrição será usada automaticamente na geração da defesa."
                value={caseDescription}
                onChange={(e) => setCaseDescription(e.target.value)}
                helperText="A descrição será automaticamente incluída quando você gerar a defesa administrativa"
                sx={{ flexGrow: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Selected Accords Section */}
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
          <GavelIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Acórdãos Selecionados
        </Typography>
        {selectedAccords.length === 0 ? (
          <Alert severity="info">
            Nenhum acórdão selecionado. Vá ao mapa para selecionar acórdãos.
          </Alert>
        ) : (
          <SelectedAccordsGrid selectedAccords={selectedAccords} onRemoveAccord={onRemoveAccord} />
        )}
      </Box>

      {/* Generate Defense Section */}
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
          <AutoAwesomeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Gerar Defesa Administrativa
        </Typography>
        
        <Button
          variant="contained"
          size="large"
          onClick={handleGenerateDefense}
          disabled={loading || (!templateUploaded && !caseUploaded && !caseDescription.trim() && selectedAccords.length === 0)}
          startIcon={
            loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <AutoAwesomeIcon />
            )
          }
          sx={{ mb: 3 }}
          fullWidth
        >
          {loading ? 'Gerando Defesa...' : 'Gerar Defesa com IA'}
        </Button>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
      </Box>

      {/* Agent Messages Display */}
      {agentMessages.length > 0 && (
        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
            <ChatIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Processo de Geração
          </Typography>
          
          <Card>
            <CardContent>
              {agentMessages.map((message, index) => (
                <Box
                  key={index}
                  sx={{
                    mb: 2,
                    p: 2,
                    backgroundColor: message.role === 'assistant' 
                      ? 'action.hover' 
                      : 'primary.light',
                    borderRadius: 1,
                    borderLeft: `4px solid ${
                      message.role === 'assistant' 
                        ? theme.palette.primary.main 
                        : theme.palette.secondary.main
                    }`
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <RobotIcon 
                      sx={{ 
                        mr: 1, 
                        fontSize: 16, 
                        color: message.role === 'assistant' 
                          ? theme.palette.primary.main 
                          : theme.palette.secondary.main 
                      }} 
                    />
                    <Typography variant="caption" color="text.secondary">
                      {getAgentDisplayName(message.role)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      '& p': { margin: '0.5em 0' },
                      '& ul, & ol': { marginLeft: '1.5em', marginTop: '0.5em', marginBottom: '0.5em' },
                      '& li': { marginBottom: '0.25em' },
                      '& code': { 
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        padding: '0.2em 0.4em',
                        borderRadius: '3px',
                        fontSize: '0.9em'
                      },
                      '& pre': {
                        backgroundColor: 'rgba(0, 0, 0, 0.05)',
                        padding: '1em',
                        borderRadius: '4px',
                        overflow: 'auto',
                        marginTop: '0.5em',
                        marginBottom: '0.5em'
                      },
                      '& pre code': {
                        backgroundColor: 'transparent',
                        padding: 0
                      },
                      '& h1, & h2, & h3, & h4, & h5, & h6': {
                        marginTop: '1em',
                        marginBottom: '0.5em',
                        fontWeight: 600
                      },
                      '& blockquote': {
                        borderLeft: '4px solid',
                        borderColor: theme.palette.primary.main,
                        paddingLeft: '1em',
                        marginLeft: 0,
                        marginTop: '0.5em',
                        marginBottom: '0.5em',
                        color: 'text.secondary'
                      },
                      '& strong': { fontWeight: 600 },
                      '& em': { fontStyle: 'italic' }
                    }}
                  >
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {message.content}
                    </ReactMarkdown>
                  </Box>
                </Box>
              ))}
              
              {loading && (
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <RobotIcon sx={{ mr: 1, fontSize: 16, color: theme.palette.primary.main }} />
                  <CircularProgress size={20} sx={{ mr: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    Agente trabalhando na geração da defesa...
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Generated Defense Display */}
      {generatedDefense && (
        <Box sx={{ mt: 4, mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
            <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Defesa Gerada
          </Typography>
          
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Documento Final
                </Typography>
                <Box>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleCopyToClipboard}
                    startIcon={<ContentCopyIcon />}
                    sx={{ mr: 1 }}
                  >
                    Copiar
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleDownloadDefense}
                    startIcon={<DownloadIcon />}
                  >
                    Baixar
                  </Button>
                </Box>
              </Box>
              <Box
                sx={{
                  backgroundColor: '#f5f5f5',
                  padding: 3,
                  borderRadius: 1,
                  maxHeight: '500px',
                  overflow: 'auto',
                  '& p': { 
                    margin: '0.75em 0',
                    fontSize: '0.875rem',
                    lineHeight: 1.6
                  },
                  '& p:first-of-type': { marginTop: 0 },
                  '& p:last-of-type': { marginBottom: 0 },
                  '& ul, & ol': { 
                    marginLeft: '1.5em', 
                    marginTop: '0.75em', 
                    marginBottom: '0.75em',
                    fontSize: '0.875rem'
                  },
                  '& li': { 
                    marginBottom: '0.5em',
                    lineHeight: 1.6
                  },
                  '& code': { 
                    backgroundColor: 'rgba(0, 0, 0, 0.08)',
                    padding: '0.2em 0.4em',
                    borderRadius: '3px',
                    fontSize: '0.85em',
                    fontFamily: 'Monaco, Consolas, "Courier New", monospace'
                  },
                  '& pre': {
                    backgroundColor: 'rgba(0, 0, 0, 0.08)',
                    padding: '1.5em',
                    borderRadius: '4px',
                    overflow: 'auto',
                    marginTop: '0.75em',
                    marginBottom: '0.75em'
                  },
                  '& pre code': {
                    backgroundColor: 'transparent',
                    padding: 0,
                    fontSize: '0.8rem'
                  },
                  '& h1': {
                    fontSize: '1.75rem',
                    marginTop: '1.5em',
                    marginBottom: '0.75em',
                    fontWeight: 700,
                    lineHeight: 1.3
                  },
                  '& h1:first-of-type': { marginTop: 0 },
                  '& h2': {
                    fontSize: '1.5rem',
                    marginTop: '1.5em',
                    marginBottom: '0.75em',
                    fontWeight: 700,
                    lineHeight: 1.3
                  },
                  '& h3': {
                    fontSize: '1.25rem',
                    marginTop: '1.25em',
                    marginBottom: '0.5em',
                    fontWeight: 600,
                    lineHeight: 1.4
                  },
                  '& h4, & h5, & h6': {
                    fontSize: '1rem',
                    marginTop: '1em',
                    marginBottom: '0.5em',
                    fontWeight: 600,
                    lineHeight: 1.4
                  },
                  '& blockquote': {
                    borderLeft: '4px solid',
                    borderColor: theme.palette.primary.main,
                    paddingLeft: '1em',
                    marginLeft: 0,
                    marginTop: '0.75em',
                    marginBottom: '0.75em',
                    color: 'text.secondary',
                    fontStyle: 'italic'
                  },
                  '& strong': { fontWeight: 700 },
                  '& em': { fontStyle: 'italic' },
                  '& hr': {
                    border: 'none',
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    marginTop: '1.5em',
                    marginBottom: '1.5em'
                  },
                  '& table': {
                    borderCollapse: 'collapse',
                    width: '100%',
                    marginTop: '0.75em',
                    marginBottom: '0.75em',
                    fontSize: '0.875rem'
                  },
                  '& th, & td': {
                    border: '1px solid',
                    borderColor: 'divider',
                    padding: '0.5em',
                    textAlign: 'left'
                  },
                  '& th': {
                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                    fontWeight: 600
                  },
                  '& a': {
                    color: theme.palette.primary.main,
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }
                }}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {generatedDefense}
                </ReactMarkdown>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}
    </Container>
  );
}

export default Defesa;
